import snakecaseKeys from 'snakecase-keys';

import { CamelCaseKeys, ProviderMedicalRole, ProviderPrescriberType, ProviderSignOnBonusTier, ProviderTerminationReason, ProviderTerminationType, UserRules } from '@uplift/types';

import { restClient } from '../../client';
import { ProviderDetail, User } from '../../resources';

export const enum ProviderActivationStatus {
  pendingInvite = 'pending_invite',
  inviteSent = 'invite_sent',
  onboarding = 'onboarding',
  readyToGo = 'ready_to_go',
  live = 'live',
}


export type ProviderActivationInviteSentTaskStatusMetadata = CamelCaseKeys<{
  reminders_sent: string[];
}>;


export const enum ProviderMatchingProfileBookmark {
  initial = 'initial',
  supportedAgeGroups = 'supported_age_groups',
  modalities = 'modalities',
  specialties = 'specialties',
  treatmentApproaches = 'treatment_approaches',
  communityExpertise = 'community_expertise',
  bio = 'bio',
  languages = 'languages',
  gender = 'gender',
  faiths = 'faiths',
  ethnicities = 'ethnicities',
  therapeuticStyles = 'therapeutic_styles',
  pronouns = 'pronouns',
  qualification = 'qualification',
  locations = 'locations',
  photo = 'photo',
  complete = 'complete',
}

export const enum ProviderActivationEventName {
  providerAdded = 'provider_added',
  signOnBonusAdded = 'sign_on_bonus_added',

  inviteSent = 'invite_sent',
  reminderSent = 'reminder_sent',

  referralSubmitted = 'referral_submitted',

  onboardingInitial = 'onboarding_initial',
  COIPDFExported = 'coi_pdf_exported',
  introductionVideoCompleted = 'introduction_video_completed',

  matchingProfileSubmitted = 'matching_profile_submitted',
  matchingProfileApproved = 'matching_profile_approved',

  matchingAvailabilitySubmitted = 'matching_availability_submitted',
  matchingAvailabilityApproved = 'matching_availability_approved',

  credentialingRequestSubmitted = 'credentialing_request_submitted',
  caqhInfoSubmitted = 'caqh_info_submitted',
  credentialingApproved = 'credentialing_approved',
  identifiersAdded = 'identifiers_added',
  insuranceAdded = 'insurance_added',
  licenseAdded = 'license_added',
  stateSelectionSubmitted = 'state_selection_submitted',
  checkrInvitationCompleted = 'checkr_invitation_completed',
  checkrReportSent = 'checkr_report_sent',
  checkrReportCompleted = 'checkr_report_completed',
  checkrReportReviewed = 'checkr_report_reviewed',

  mfaAdded = 'mfa_added',
  stripeConnectInProgress = 'stripe_connect_in_progress',
  stripeConnectCompleted = 'stripe_connect_completed',
  securityTrainingSent = 'security_training_sent',
  securityTrainingReviewed = 'security_training_reviewed',
  hipaaTrainingSent = 'hipaa_training_sent',
  hipaaTrainingReviewed = 'hipaa_training_reviewed',
  clinicalTrainingSent = 'clinical_training_sent',
  clinicalTrainingReviewed = 'clinical_training_reviewed',
  treatInviteSent = 'treat_invite_sent',
  labVendorInvitesSent = 'lab_vendor_invites_sent',

  readyToGo = 'ready_to_go',
  live = 'live',
  confettiDisplayed = 'confetti_displayed',

  deactivated = 'deactivated',

  terminated = 'terminated',
}

export type ProviderActivationEvent = {
  providerId: string;
  eventName: ProviderActivationEventName;
  createdBy: string;
  createdAt: string;
  metadata?: {
    referralId?: string;
    terminationType?: ProviderTerminationType;
    terminationReason?: ProviderTerminationReason;
    terminationDescription?: string;
  };
};

type BaseProviderActivationStatusDetails<
  Status extends ProviderActivationStatus,
  Extra extends Record<string, unknown> = {}
> = CamelCaseKeys<{
  provider_id: string;
  status: Status;
  status_changed_at: string | null;
  events: ProviderActivationEvent[];
} & Extra>;

export type ProviderActivationTasks = {
  tasksToDo: ProviderActivationEventName[];
  tasksToDoOptional: ProviderActivationEventName[];
  tasksToBeApproved: ProviderActivationEventName[];
};


export type ActivationStatusDetailsPendingInvite = BaseProviderActivationStatusDetails<ProviderActivationStatus.pendingInvite>;
export type ActivationStatusDetailsInviteSent = BaseProviderActivationStatusDetails<ProviderActivationStatus.inviteSent, {
  reminders_count: number;
  reminder_sent_at: string;
  invite_sent_at: string;
} | {
  reminders_count: 0;
  reminder_sent_at: null;
  invite_sent_at: string;
}>;
export type ActivationStatusDetailsOnboarding = BaseProviderActivationStatusDetails<ProviderActivationStatus.onboarding>;
export type ActivationStatusDetailsReadyToGo = BaseProviderActivationStatusDetails<ProviderActivationStatus.readyToGo>;
export type ActivationStatusDetailsLive = BaseProviderActivationStatusDetails<ProviderActivationStatus.live>;
export type ActivationStatusDetailsByStatus = {
  [ProviderActivationStatus.pendingInvite]: ActivationStatusDetailsPendingInvite;
  [ProviderActivationStatus.inviteSent]: ActivationStatusDetailsInviteSent;
  [ProviderActivationStatus.onboarding]: ActivationStatusDetailsOnboarding;
  [ProviderActivationStatus.readyToGo]: ActivationStatusDetailsReadyToGo;
  [ProviderActivationStatus.live]: ActivationStatusDetailsLive;
};
export type ProviderActivationStatusDetails = ActivationStatusDetailsByStatus[keyof ActivationStatusDetailsByStatus];
export type ProviderActivationItem<T extends ProviderActivationStatusDetails> = CamelCaseKeys<{
  provider: User;
  licenseStateIds: string[];
  details: ProviderDetail;
  rules: UserRules;
  status_details: T;
}>;

export const adminGetProvidersActivation = () => restClient.request<{
  data: ProviderActivationItem<ProviderActivationStatusDetails>[];
}>('/api/v1/admin/provider_activations');

export const adminGetProviderActivation = (providerId: string) => restClient.request<{
  data: ProviderActivationStatusDetails;
}>(`/api/v1/admin/provider_activation/${providerId}`, { method: 'GET' });


export const adminAddProviderActivationEvent = (providerId: string, eventName: ProviderActivationEventName) => restClient.request<{
  data: null;
}>(`/api/v1/admin/provider_activation/${providerId}/events`, {
  method: 'POST',
  data: {
    event_name: eventName,
  }
});

export const adminRemoveProviderActivationEvent = (providerId: string, eventName: ProviderActivationEventName) => restClient.request<{
  data: null;
}>(`/api/v1/admin/provider_activation/${providerId}/events`, {
  method: 'DELETE',
  data: {
    event_name: eventName,
  }
});

export const adminGetProviderActivationTasks = (activationStatus: ProviderActivationStatus) => restClient.request<{
  data: ProviderActivationTasks;
}>(`/api/v1/admin/provider_activation/${activationStatus}/tasks`);

export type GetProviderActivationEventsResponse = {
  data: ProviderActivationEvent[];
};

export const adminGetProviderActivationEvents = (providerId: string) =>
  restClient.request<GetProviderActivationEventsResponse>(`/api/v1/admin/provider_activation/${providerId}/events`);

export type Tag = {
  id: string;
  type: string;
  label: string | null;
};


export const getMSOData = () => restClient.request<{
  data: {
    partners: Tag[];
    groups: Tag[];
  };
}>('/api/v1/admin/mso');


export const adminAddProvider = (data: {
  firstName: string;
  lastName: string;
  emailAddress: string;
  fullTime: boolean;
  medicalRole: ProviderMedicalRole;
  prescriberType: ProviderPrescriberType | null;
  collaborativePhysician: boolean;
  msoPartner: string | null;
  msoGroups: string[] | null;
  tagIds?: string[] | null;
  signOnBonusTier: ProviderSignOnBonusTier | null;
}) =>
  restClient.request('/api/v1/admin/add_provider', {
    method: 'POST',
    data: snakecaseKeys(data),
  });

export const adminAddProviderSignOnBonus = (providerId: string, signOnBonusTier: ProviderSignOnBonusTier) =>
  restClient.request<{ data: null }>(`/api/v1/admin/providers/${providerId}/sign_on_bonus`, {
    method: 'POST',
    data: { sign_on_bonus_tier: signOnBonusTier },
  });
